import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useForm } from "react-hook-form";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { FiUploadCloud } from "react-icons/fi";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ICertificatesDocumentUpdatePutBody } from "@/api_framework/api_modals/Certificates";
import { ROUTES } from "@/app/routes";
import { useFileToS3LinkGeneratorAPI } from "@/app/hooks/api_hooks/LabReport/useFileToS3LinkGeneratorAPI";
import { toast } from "react-hot-toast";
import { useDocumentListAPI } from "@/app/hooks/api_hooks/CertificateDocument/useDocumentListAPI";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { IoCamera } from "react-icons/io5";
import { useDocumentUpdateAPI } from "@/app/hooks/api_hooks/CertificateDocument/useDocumentUpdateAPI";
import { MdOutlineFlipCameraIos } from "react-icons/md";
import { useNavigate } from "react-router";

const certificates = [
  { id: "1", title: "Job Card", sub: "Report" },
  { id: "2", title: "Patient Document", sub: "Report" },
  { id: "3", title: "General Consent Form", sub: "" },
  { id: "4", title: "X-RAY", sub: "" },
  { id: "5", title: "ULTRASOUND", sub: "ULTRASOUND" },
  { id: "6", title: "Feline Immunodeficiency Virus / Feline Leukemia Virus Antigen Test", sub: "Report" },
  { id: "7", title: "Canine Parvo-Corona-Giardia Antigen Kit test", sub: "Report" },
  { id: "8", title: "Ehrlichia-Anaplasa-Babesia-Heartworm Antibody Kit test", sub: "Report" },
  { id: "9", title: "Canine Distemper-Parvo-Corona-Giardia Combo Rapid test", sub: "Report" },
  { id: "10", title: "Feline Panleukopenia-Feline Corona-Giardia Kit test", sub: "Report" },
  { id: "11", title: "Canine Parvo", sub: "Report" },
  { id: "12", title: "Canine Parvo Distemper", sub: "Report" },
  { id: "13", title: "Feline Infectious Peritonitis Antibody Test", sub: "Report" },
  { id: "14", title: "Feline Panleukopenia Antigen Test Kit", sub: "Report" },
  { id: "15", title: "Death Certificate", sub: "Report" },
  { id: "16", title: "EUTHANASIA CONSENT FORM", sub: "Report" },
  { id: "17", title: "FREEDOM FROM DISEASE DECLARATION", sub: "Report" },
  { id: "18", title: "PET HEALTH CUM TRAVEL CERTIFICATE", sub: "Report" },
  { id: "19", title: "MICROCHIPPING CERTIFICATE", sub: "Report" },
  { id: "20", title: "PET VACCINATION CERTIFICATE", sub: "Report" },
  { id: "21", title: "CERTIFICATE OF STERILIZATION", sub: "Report" },
  { id: "22", title: "Boarding and Hospitalisation Agreement", sub: "Report" },
  { id: "23", title: "PET HEALTH CERTIFICATE", sub: "Report" },
  { id: "24", title: "Sedation, or Anaesthesia and Surgery Consent Form", sub: "Report" },
  { id: "25", title: "X-Ray/USG Consent Form", sub: "Report" },
  { id: "26", title: "Blood glucose", sub: "Report" },
  { id: "27", title: "Urine Routine Analysis", sub: "Report" },
  { id: "28", title: "Ear Swab for Mite", sub: "Report" },
  { id: "29", title: "BP measurement", sub: "Report" },
  { id: "30", title: "Skin Scraping Microscopic", sub: "Report" },
  { id: "31", title: "ECHOCARDIOGRAPHY", sub: "Report" },
];

export interface ICertificateDocumentEditModalProps {
  documents?: string[];
  pet_name: string;
  pet_id: string;
  certificate_type: string;
  certificate_id: string;
  remarks: string;
}

export const compressImageWithoutLib = async (file: File): Promise<File> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const MAX_WIDTH = 800; // you can tune this
        const scale = Math.min(1, MAX_WIDTH / img.width);

        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

        // compress to JPEG with quality (0.7 = 70%)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });

              console.log(
                `Compressed: ${file.name}, Original: ${(file.size / 1024 / 1024).toFixed(2)} MB, Compressed: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`,
              );

              resolve(compressedFile);
            }
          },
          "image/jpeg",
          0.7, // adjust quality here
        );
      };
    };
  });
};

const CertificateDocumentEditModalContent = ({ formHook, data }) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerPets } = routeState.state as ICustomerProfileState;
  const petItems = useSelectMenuReducer(customerPets, "name", "id", {
    stateKey: "status",
  });
  const { execute: uploadToS3 } = useFileToS3LinkGeneratorAPI();
  const documentFiles = formHook.watch("documents") || [];
  const [cameraAvailable, setCameraAvailable] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [_capturedImage, setCapturedImage] = useState<File | null>(null);

  // Convert HEIC to JPEG using canvas
  const convertHeicToJpeg = async (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const jpegFile = new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                });
                console.log(
                  `Converted HEIC -> JPEG: ${jpegFile.name}, Size: ${(jpegFile.size / 1024 / 1024).toFixed(2)} MB`,
                );
                resolve(jpegFile);
              }
            },
            "image/jpeg",
            0.9,
          );
        };
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const MAX_FILE_SIZE_MB = 10;
    const files = Array.from(event.target.files);
    const processedFiles: File[] = [];

    for (const file of files) {
      let processedFile = file;
      const originalSizeMB = processedFile.size / 1024 / 1024;
      if (originalSizeMB > MAX_FILE_SIZE_MB) {
        toast.error(`${processedFile.name} exceeds 10 MB limit.`);
        continue; // skip this file
      }
      if (processedFile.type === "image/heic" || processedFile.type === "image/heif") {
        try {
          processedFile = await convertHeicToJpeg(file);
        } catch (err) {
          toast.error(`Failed to convert HEIC file: ${file.name}`);
          continue;
        }
      }
      if (processedFile.type.startsWith("image/")) {
        processedFile = await compressImageWithoutLib(processedFile);
      }     
      const isDuplicate = uploadedFiles.some((uf) => uf.name === processedFile.name && uf.size === processedFile.size);
      if (isDuplicate) {
        toast.error(`Duplicate file not allowed: ${processedFile.name}`);
        continue;
      }

      processedFiles.push(processedFile);
    }

    if (processedFiles.length === 0) {
      event.target.value = ""; // reset input
      return;
    }

    // if (event.target.files && event.target.files.length > 0) {
    // const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    // const images = Array.from(event.target.files);
    // const invalidFiles = images.filter((file) => !allowedTypes.includes(file.type));
    // if (invalidFiles.length > 0) {
    //   toast.error("Only JPG, PNG, and PDF files are allowed.");
    //   event.target.value = "";
    //   return;
    // }
    // const files = Array.from(event.target.files);
    const uploadedImages = await Promise.all(
      processedFiles.map(async (file) => {
        const result = await uploadToS3(file, formHook.getValues("certificate_id"), "certificate");
        if (result.status) {
          return result.message;
        }
        toast.error(`Failed to upload ${file.name}`);
        return null;
      }),
    );
    const validImages = uploadedImages.filter((img) => img !== null);
    const currentImages = formHook.getValues("documents") || [];
    formHook.setValue("documents", [...currentImages, ...new Set(validImages?.flat())]);
    setUploadedFiles([...uploadedFiles, ...files]);
    // }
  };

  useEffect(() => {
    if (data) {
      formHook.setValue("certificate_type", data?.documents?.document_type);
      formHook.setValue("certificate_id", data?.documents?.document_id);
      formHook.setValue("pet_name", data?.documents?.pet_name);
      formHook.setValue("pet_id", data?.documents?.pet_id);
      formHook.setValue("remarks", data?.documents?.remarks);
      formHook.setValue("documents", data?.documents?.document_files || []);
    }
    setUploadedFiles([]);
  }, [data]);

  const stopCameraStream = () => {
    const video = videoRef.current;
    if (video && video.srcObject) {
      const stream = video.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      const ctx = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `photo-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          setCapturedImage(file);
          handleFileChange({ target: { files: [file] } } as any);
          stopCameraStream();
          setShowCamera(false);
        }
      }, "image/jpeg");
    }
  };

  useEffect(() => {
    checkCameraAvailability().then(setCameraAvailable);
  }, []);

  const checkCameraAvailability = async (): Promise<boolean> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some((device) => device.kind === "videoinput");
    } catch (err) {
      console.error("Camera check failed:", err);
      return false;
    }
  };

  useEffect(() => {
    if (showCamera && videoRef.current) {
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode } })
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch((err) => {
          toast.error("Unable to access camera");
          console.error(err);
          setShowCamera(false);
        });
    }
  }, [showCamera, facingMode]);

  return (
    <div>
      <div className="items-center sm:gap-16 sm:flex mt-3">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] shrink-0">
          <span className="font-normal text-black text-xs">Select Certificate</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <div className="w-full sm:w-72">
          <SearchableSelectMenu
            errorMessages={[{ message: "", type: "required" }]}
            onSelectItem={(item) => {
              if (item) {
                formHook.setValue("certificate_type", item.title);
                formHook.setValue("certificate_id", item.id);
              }
            }}
            fieldError={formHook.formState.errors.certificate_type}
            register={formHook.register("certificate_type", { required: true })}
            selectItems={certificates}
            placeholder="Select Certificate"
            showTooltips={true}
            showTypedErrors={false}
            showDropdownIcon={true}
            defaultSelected={certificates?.find((oc) => oc.title === formHook.watch("certificate_type"))}
            listBoxClassName="w-full"
            className="text-gray-800 w-full"
            disabled={true}
          />
        </div>
      </div>
      <div className="items-center sm:gap-16 sm:flex mt-5">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] shrink-0">
          <span className="font-normal text-black text-xs">Select Pet</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <div className="w-full sm:w-72">
          <SearchableSelectMenu
            errorMessages={[{ message: "", type: "required" }]}
            onSelectItem={(item) => {
              if (item) {
                formHook.setValue("pet_name", item.title);
                formHook.setValue("pet_id", item.id);
              }
            }}
            fieldError={formHook.formState.errors.pet_name}
            register={formHook.register("pet_name", { required: true })}
            selectItems={petItems}
            placeholder="Select Pet"
            showTooltips={true}
            showTypedErrors={false}
            showDropdownIcon={true}
            defaultSelected={petItems?.find((oc) => oc.id === formHook.watch("pet_id"))}
            listBoxClassName="w-full"
            className="text-gray-800 w-full"
            disabled={true}
          />
        </div>
      </div>
      <div className="justify-start items-center sm:gap-16 sm:flex mt-5 mb-5">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] shrink-0">
          <span className="font-normal text-black text-xs">Add Remarks</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <div className="w-full sm:w-72">
          <textarea
            style={{
              overflowY: "scroll",
              scrollbarWidth: "thin",
            }}
            rows={3}
            className={clsx(`rounded-md w-full h-full border-gray-300 text-xs `)}
            placeholder="Add Remarks"
            {...formHook.register(`remarks`, { required: true, minLength: 1 })}
          />
        </div>
      </div>
      <div className="justify-start items-center sm:gap-16 sm:flex mb-5">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] shrink-0">
          <span className="font-normal text-black text-xs">Document</span>
        </div>
        <div className="w-full sm:w-72">
          <div
            className="flex gap-2 items-baseline border border-gray-300 py-3 px-3 rounded-md justify-center cursor-pointer"
            onClick={() => document.getElementById("signed-doc-upload")?.click()}
          >
            <FiUploadCloud size="15" />
            <p className="text-sm font-semibold text-[#1364ff] cursor-pointer">Re-Upload Signed Document</p>
            <input
              type="file"
              id="signed-doc-upload"
              className="hidden"
              multiple
              accept=".png,.jpeg,.jpg,.pdf"
              onChange={handleFileChange}
            />
          </div>
          <div className="text-center">OR</div>
          {cameraAvailable && (
            <div
              className="flex gap-2 items-center border border-gray-300 py-3 px-4 sm:px-10 rounded-md justify-center cursor-pointer"
              onClick={() => setShowCamera(true)}
            >
              <IoCamera />
              <p className="text-sm font-semibold text-[#1364ff] cursor-pointer">Take Photo</p>
            </div>
          )}
        </div>
      </div>
      {showCamera && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col justify-between sm:px-10 sm:pt-10 sm:pb-24 md:px-3 md:pt-3 md:pb-24 items-center pb-3 pt-3 px-3">
          <button
            onClick={() => {
              stopCameraStream();
              setShowCamera(false);
            }}
            className="absolute top-4 right-4 text-white text-2xl font-bold z-10"
            aria-label="Close camera"
          >
            &times;
          </button>
          <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <div className="w-full p-4 bg-black flex justify-center items-center gap-6">
            {navigator?.userAgent?.match(/Mobi|Android|iPhone|iPad/i) && (
              <button
                onClick={() => {
                  stopCameraStream();
                  setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
                }}
                className="text-white text-sm px-3 py-1 items-center rounded sm:text-[20px] md:text-[30px] text-[30px]"
              >
                <MdOutlineFlipCameraIos color="white" />
              </button>
            )}
            <button
              className="sm:w-16 sm:h-16 w-20 h-20 bg-white rounded-full border-4 border-gray-400 shadow-md flex items-center justify-center"
              onClick={handleCapture}
            >
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
            </button>
          </div>
        </div>
      )}
      {documentFiles.length > 0 && (
        <div className="flex gap-3 mt-4 flex-wrap">
          {documentFiles.map((fileUrl, index) => (
            <div key={index} className="relative w-24 h-24 rounded overflow-hidden">
              <button
                type="button"
                className="absolute top-0.5 right-0.5 z-10 bg-white border border-gray-300 rounded-full w-5 h-5 text-sm flex items-center justify-center hover:bg-gray-100"
                onClick={() => {
                  const updatedFiles = documentFiles.filter((_, i) => i !== index);
                  formHook.setValue("documents", updatedFiles);
                }}
              >
                ×
              </button>
              {fileUrl.endsWith(".pdf") ? (
                <>
                  <img
                    src={"https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/pdf-svgrepo-com.svg"}
                    alt={"pdfs"}
                    className="w-full h-full object-cover"
                  />
                  <p className="text-xs text-gray-800 px-2 py-1">{fileUrl.name}</p>
                </>
              ) : (
                <>
                  <img src={fileUrl} alt={`document-${index}`} className="object-cover w-full h-full" />
                  <p className="text-xs text-gray-800 px-2 py-1">{fileUrl.name}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CertificateDocumentEditModal = () => {
  const { setInvisible } = useModalActions("CERTIFICATE_DOCUMENT_EDIT_MODAL");
  const { execute: updateCertificate } = useDocumentUpdateAPI();
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { customerId } = routeState.state as ICustomerProfileState;
  const { execute: fetchDocumentList } = useDocumentListAPI();
  const modalState = useModalState("CERTIFICATE_DOCUMENT_EDIT_MODAL");
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  const formHook = useForm<ICertificateDocumentEditModalProps>({
    mode: "onChange",
    defaultValues: {
      pet_id: "",
      pet_name: "",
      certificate_id: "",
      certificate_type: "",
      remarks: "",
      documents: [],
    },
  });

  const handleClose = () => {
    formHook.resetField("pet_id");
    formHook.resetField("pet_name");
    formHook.resetField("certificate_type");
    formHook.resetField("certificate_id");
    formHook.resetField("documents");
    formHook.resetField("remarks");
    setInvisible();
  };

  const handleSubmit = async (data: ICertificateDocumentEditModalProps) => {
    setIsUpdating(true);
    const constructedBody: ICertificatesDocumentUpdatePutBody = {
      document_id: modalState?.documentId,
      remarks: data?.remarks,
      document_files: data.documents,
    };
    try {
      const response = await updateCertificate(constructedBody);
      if (response?.status) {
        fetchDocumentList(customerId);
      }
    } finally {
      setInvisible();
      if (modalState?.returnPath) {
        navigate(modalState.returnPath);
      }
      setIsUpdating(false);
    }
  };

  return (
    <ModalPrompt
      modalId="CERTIFICATE_DOCUMENT_EDIT_MODAL"
      showIcon={false}
      titleIcon=""
      dialogClassName="w-full sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
      button={{
        primaryButtonText: isUpdating ? "Updating..." : "Update",
        secondaryButtonText: "Cancel",
        primaryButtonClassname: `px-6 py-[9px] rounded-md border border-slate-400 border-opacity-20 justify-center items-center gap-2 flex cursor-pointer bg-sky-900 hover`,
        primaryButtonDisabled:
          isUpdating ||
          formHook.watch("pet_id")?.length == 0 ||
          formHook.watch("documents")?.length == 0 ||
          formHook.watch("certificate_id")?.length == 0,
        secondaryButtonDisabled: isUpdating,
        primaryCallback: () => {
          handleSubmit(formHook.getValues());
        },
        secondaryCallback: () => {
          formHook.clearErrors();
          handleClose();
        },
      }}
      title="Edit Document"
      content={<CertificateDocumentEditModalContent formHook={formHook} data={modalState} />}
    />
  );
};

export default CertificateDocumentEditModal;
